import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useSiteContent, mergeServices } from "../lib/siteContent";

const imgs = [
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB3ZWxsbmVzcyUyMG9mZmljZSUyMHRlYW0lMjB3b3Jrc2hvcHxlbnwwfHx8fDE3Nzg4NDI4MDZ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1630834940713-08078c3d679e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxjYWxtJTIwdGhlcmFweSUyMHNlc3Npb24lMjBoYW5kcyUyMGludGVyaW9yfGVufDB8fHx8MTc3ODg0MjgwNnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1730342754575-0c8ca8e62c0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxjYWxtJTIwdGhlcmFweSUyMHNlc3Npb24lMjBoYW5kcyUyMGludGVyaW9yfGVufDB8fHx8MTc3ODg0MjgwNnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1607835017779-c176b83b2dd8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxjYWxtJTIwdGhlcmFweSUyMHNlc3Npb24lMjBoYW5kcyUyMGludGVyaW9yfGVufDB8fHx8MTc3ODg0MjgwNnww&ixlib=rb-4.1.0&q=85",
];

export default function Services() {
  const { t, lang } = useLang();
  const { hash } = useLocation();
  const { content } = useSiteContent();
  const items = mergeServices(t.services.items, lang, content?.services);

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [hash]);

  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="container-x pt-12 pb-16 md:pt-20 md:pb-24">
        <span className="label-eyebrow">{t.services.eyebrow}</span>
        <h1 className="mt-5 font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight text-ink max-w-4xl">
          {t.services.title}
        </h1>
        <p className="mt-6 text-lg md:text-xl text-ink/70 font-light leading-relaxed max-w-3xl">
          {t.services.lead}
        </p>
      </section>

      {/* DETAILED SERVICE BLOCKS */}
      {items.map((s, i) => (
        <section
          key={s.slug}
          id={s.slug}
          data-testid={`service-block-${s.slug}`}
          className={`border-t border-cream-300/70 ${i % 2 === 1 ? "bg-cream-200/40" : ""}`}
        >
          <div className="container-x py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
            <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
              <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-float">
                <img src={s.image || imgs[i % imgs.length]} alt={s.t} className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="lg:col-span-6">
              <span className="text-xs uppercase tracking-[0.22em] text-terracotta-400 font-medium">{s.eyebrow}</span>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl leading-tight tracking-tight text-ink">{s.t}</h2>
              <p className="mt-5 text-lg text-ink/70 font-light leading-relaxed">{s.long}</p>
              <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-ink/80">
                    <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-sage-100 text-sage-700">
                      <Check className="w-3 h-3" strokeWidth={2} />
                    </span>
                    <span className="font-light">{b}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                data-testid={`service-cta-${s.slug}`}
                className="mt-9 inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-terracotta-400 transition-colors group"
              >
                {t.common.getInTouch}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </section>
      ))}

      {/* PROCESS */}
      <section className="container-x py-24 md:py-32">
        <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink max-w-2xl">
          {t.services.process.title}
        </h2>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {t.services.process.steps.map((step, i) => (
            <div
              key={step.t}
              data-testid={`process-step-${i}`}
              className="relative rounded-2xl p-7 bg-cream-200/60 border border-cream-300/70 hover:bg-white hover:shadow-soft transition-all"
            >
              <span className="font-serif text-5xl text-terracotta-400/70 leading-none">0{i + 1}</span>
              <h3 className="mt-4 font-serif text-2xl text-ink">{step.t}</h3>
              <p className="mt-2 text-sm font-light text-ink/65 leading-relaxed">{step.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
